import type { RestrictionOffer } from "./variantRestrictionUpload";
import type { StratumPlan } from "./bayPartition";

export interface StudioPlacement {
  run: number;
  shelf: number;
  /** The 1-based bay the source authored this placement into; absent when the source
   *  has no bays. Wall readers prefer it over dividing `xCm` by the bay width, because
   *  a merged run re-derives x and can render an item inside its neighbour's span. */
  bay?: number;
  xCm: number;
  widthCm: number;
  hFacings: number;
}

export interface StudioItem {
  skuId: string;
  itemMasterId?: string;
  upc: string;
  name: string;
  run: number;
  facingWidthCm: number;
  heightCm: number;
  facings: number;
  /** Attribute values keyed by spine attribute; null = unknown. */
  attrs: Record<string, string | null>;
  /** Keys of `attrs` supplied by the item-master overlay (including a semantic attribute
   *  a section mapping filled from an overlay column), in the master's column order.
   *  When present it is the whole candidate pool for level inference; absent when the
   *  item was built from the POG's columns alone. */
  overlayAttrs?: readonly string[];
  dosDays?: number;
  dosPerFacing?: number;
  maxFacings?: number;
  salesAmount?: number;
  /** Absent leaves the weight ordering rule inert. */
  weightKg?: number;
  placements: readonly StudioPlacement[];
}

export interface StudioShelfGeometry {
  rank: number;
  yCm: number;
  thicknessCm: number;
}

export interface StudioRunGeometry {
  run: number;
  name: string;
  widthCm: number;
  heightCm: number;
  bayWidthCm: number;
  bays: number;
  shelves: readonly StudioShelfGeometry[];
}

export type Orientation = "horizontal" | "vertical";

export interface SpineLevel {
  attr: string;
  label: string;
  orient: Orientation;
}

export interface AppliedPromotion {
  attr: string;
  parentValue: string;
  bandVariant: string;
  newValues: readonly string[];
  skuIds: readonly string[];
  previousValueOrder?: readonly string[];
  previousRootOrder?: readonly string[];
}

export interface StudioDraft {
  /** The ship-time measurement must replay without splitting its own bands again. */
  measuredBands?: { sectionAttr: string; strata: readonly StratumPlan[] };
  spine: readonly SpineLevel[];
  valueOrder: Readonly<Record<string, readonly string[]>>;
  /** Keyed by parent scope path (root = ""); wins over `valueOrder`. */
  scopedValueOrder: Readonly<Record<string, readonly string[]>>;
  /** A preference, not a fence: the pour falls back to neighbouring lanes, and no entry means a width-share slice. */
  bandOverrides: Readonly<Record<string, readonly number[]>>;
  orientOverrides: Readonly<Record<string, Orientation>>;
  /** Per-branch level order from this scope's depth down, present ONLY where a branch differs from
   *  `spine` (which stays the topological merge the vendored resolver reads). `collapse4` must
   *  resolve tiers through `levelsForScope`, or a ragged branch ships `cdt1 = ""`. */
  scopedLevelOrder?: Readonly<Record<string, readonly string[]>>;
  /** Used only while the SKU's node still covers those shelves. */
  shelfHints: Readonly<Record<string, readonly { shelf: number; hFacings: number }[]>>;
  attrOverrides: Readonly<Record<string, Readonly<Record<string, string | null>>>>;
  promotions?: readonly AppliedPromotion[];
  levelBands?: Readonly<Record<string, Readonly<Record<string, string>>>>;
  poolingOverrides?: Readonly<Record<string, "vertical" | "pooled">>;
  restrictions: readonly AcceptedRestriction[];
  dismissedFlagIds: readonly string[];
  /** Keyed by CONTENT-derived rule id — never a list index, which renumbers. */
  placementRuleDecisions?: Readonly<Record<string, "keep" | "decline">>;
  /** Keeping an offer applies its promotion, so finalize writes from this snapshot, not a re-derivation. */
  keptPlacementOffers?: readonly RestrictionOffer[];
  acceptedFlagIds?: readonly string[];
  appliedFixFlagIds: readonly string[];
}

export const PATH_SEPARATOR = " › ";

/** Values meaning "no data here". A placeholder holds NO POSITION in a level's order, so ranking,
 *  comparing and banding must SKIP it rather than treat it as a value of its own. */
export const PLACEHOLDER_VALUE = /^(unspecified|n[/-]?a|none|unknown|)$/i;

export const isPlaceholderValue = (value: string | null | undefined): boolean =>
  PLACEHOLDER_VALUE.test(String(value ?? "").trim());

/** The level sequence a SCOPE splits by; overrides reorder levels, never rename them. Every
 *  positional tree walk must go through this or the parts will disagree about a branch's rungs. */
export const levelsForScope = (
  spine: readonly SpineLevel[],
  scopedLevelOrder: Readonly<Record<string, readonly string[]>> | undefined,
  path: readonly string[],
): readonly SpineLevel[] => {
  if (!scopedLevelOrder) return spine;
  /* Overrides COMPOSE, shallowest first: each reorders what its ancestors settled. */
  let levels = spine;
  for (let depth = 0; depth <= path.length; depth += 1) {
    const order = scopedLevelOrder[path.slice(0, depth).join(PATH_SEPARATOR)];
    if (!order) continue;
    const byAttr = new Map(levels.map((level) => [level.attr, level]));
    const reordered = order.flatMap((attr) => {
      const level = byAttr.get(attr);
      return level ? [level] : [];
    });
    levels = [...levels.slice(0, depth), ...reordered];
  }
  return levels;
};

/** Which path-keyed contract keys a carve renames. Each carved key is derived the way the POST-CARVE
 *  walk derives it: segments resolve down the branch own level sequence against the band members and
 *  survive only while their level still splits them. The parent one-segment key is never mapped. */
export const carvedKeyMapForPromotion = (
  items: readonly StudioItem[],
  draft: Pick<StudioDraft, "spine" | "attrOverrides"> &
    Partial<Pick<StudioDraft, "scopedLevelOrder" | "levelBands">>,
  carve: { parentValue: string; bandVariant: string; skuIds: readonly string[] },
  keys: readonly string[],
): Readonly<Record<string, string>> => {
  const band = new Set(carve.skuIds);
  const bandItems = items.filter((item) => band.has(item.skuId));
  if (bandItems.length === 0) return {};

  const attrsOf = new Map<string, Record<string, string | null>>();
  const attrOf = (item: StudioItem, attr: string): string => {
    let resolved = attrsOf.get(item.skuId);
    if (!resolved) {
      resolved = effectiveAttrs(item, draft);
      attrsOf.set(item.skuId, resolved);
    }
    return String(resolved[attr] ?? "");
  };
  const discriminates = (scope: readonly StudioItem[], attr: string): boolean => {
    const seen = new Set<string>();
    for (const item of scope) {
      const value = attrOf(item, attr);
      if (isPlaceholderValue(value)) continue;
      seen.add(value);
      if (seen.size >= 2) return true;
    }
    return false;
  };

  const out: Record<string, string> = {};
  for (const key of keys) {
    if (key in out) continue;
    const segments = key.split(PATH_SEPARATOR);
    if (segments[0] !== carve.parentValue || segments.length < 2) continue;

    let scope = bandItems;
    let levelIndex = 1;
    let belongs = true;
    const kept: string[] = [carve.bandVariant];
    for (let index = 1; index < segments.length; index += 1) {
      const levels = levelsForScope(draft.spine, draft.scopedLevelOrder, segments.slice(0, index));
      let matched: StudioItem[] | null = null;
      while (levelIndex < levels.length) {
        const attr = levels[levelIndex].attr;
        levelIndex += 1;
        const filtered = scope.filter((item) => attrOf(item, attr) === segments[index]);
        if (filtered.length > 0) {
          if (discriminates(scope, attr)) kept.push(segments[index]);
          matched = filtered;
          break;
        }
      }
      if (!matched) {
        belongs = false;
        break;
      }
      scope = matched;
    }
    if (belongs) out[key] = kept.join(PATH_SEPARATOR);
  }
  return out;
};

/** The carved ROOT inherits the PARENT entry when no band branch supplied one; the parent keeps its own. */
export const rekeyScopedLevelOrderForPromotion = (
  scopedLevelOrder: Readonly<Record<string, readonly string[]>> | undefined,
  keyMap: Readonly<Record<string, string>>,
  parentValue: string,
  bandVariant: string,
): Readonly<Record<string, readonly string[]>> | undefined => {
  if (!scopedLevelOrder) return undefined;
  let out = rekeyScopedMapForPromotion(scopedLevelOrder, keyMap);
  const parentOrder = scopedLevelOrder[parentValue];
  if (parentOrder && !(bandVariant in out)) out = { ...out, [bandVariant]: parentOrder };
  return Object.keys(out).length > 0 ? out : undefined;
};

export const dropScopedLevelOrderForPromotion = (
  scopedLevelOrder: Readonly<Record<string, readonly string[]>> | undefined,
  bandVariant: string,
): Readonly<Record<string, readonly string[]>> | undefined => {
  if (!scopedLevelOrder) return undefined;
  const out = dropScopedMapForPromotion(scopedLevelOrder, bandVariant);
  return Object.keys(out).length > 0 ? out : undefined;
};

/** A promotion is a RENAME, NOT A RE-MEASUREMENT, so the SKUs under the carved key are the ones the
 *  entry was measured on. Returns the SAME reference when nothing carries. */
export const rekeyScopedMapForPromotion = <T>(
  map: Readonly<Record<string, T>>,
  keyMap: Readonly<Record<string, string>>,
): Readonly<Record<string, T>> => {
  const carved: Record<string, T> = {};
  for (const [key, carvedKey] of Object.entries(keyMap)) {
    if (!(key in map)) continue;
    if (carvedKey in map || carvedKey in carved) continue;
    carved[carvedKey] = map[key];
  }
  return Object.keys(carved).length > 0 ? { ...map, ...carved } : map;
};

export const dropScopedMapForPromotion = <T>(
  map: Readonly<Record<string, T>>,
  bandVariant: string,
): Readonly<Record<string, T>> => {
  if (!Object.keys(map).some((key) => key.split(PATH_SEPARATOR)[0] === bandVariant)) return map;
  const out: Record<string, T> = {};
  for (const [key, value] of Object.entries(map)) {
    if (key.split(PATH_SEPARATOR)[0] === bandVariant) continue;
    out[key] = value;
  }
  return out;
};

/** An item's SLAB PATH: the leading run of internal-"horizontal" levels along the item's OWN
 *  branch, clamped to at least one and at most three; `bandAttr` is the level directly below it.
 *  Block identity must come through this, not a positional `spine.slice`. */
export const slabPathOfAttrs = (
  spine: readonly SpineLevel[],
  scopedLevelOrder: Readonly<Record<string, readonly string[]>> | undefined,
  attrs: Record<string, string | null>,
): { values: readonly string[]; bandAttr: string | null } => {
  const values: string[] = [];
  let levels = levelsForScope(spine, scopedLevelOrder, values);
  while (values.length < 3) {
    const level = levels[values.length];
    if (!level || level.orient !== "horizontal") break;
    values.push(String(attrs[level.attr] ?? ""));
    levels = levelsForScope(spine, scopedLevelOrder, values);
  }
  if (values.length === 0 && levels.length > 0) {
    values.push(String(attrs[levels[0].attr] ?? ""));
    levels = levelsForScope(spine, scopedLevelOrder, values);
  }
  return { values, bandAttr: levels[values.length]?.attr ?? null };
};

export const effectiveAttrs = (
  item: StudioItem,
  draft: Pick<StudioDraft, "attrOverrides"> & Partial<Pick<StudioDraft, "levelBands">>,
): Record<string, string | null> => {
  const override = draft.attrOverrides[item.skuId];
  const bands = draft.levelBands;
  // No banded level: return the item's own attrs BY REFERENCE, so nothing downstream sees a copy.
  if (!bands || Object.keys(bands).length === 0) {
    return override ? { ...item.attrs, ...override } : item.attrs;
  }
  const banded: Record<string, string | null> = { ...item.attrs, ...override };
  for (const [attr, bandOf] of Object.entries(bands)) {
    const raw = banded[attr];
    if (raw == null) continue;
    const band = bandOf[raw];
    if (band !== undefined) banded[attr] = band;
  }
  return banded;
};

export interface GeneratedSegment {
  run: number;
  shelf: number;
  xCm: number;
  hFacings: number;
}

export interface GeneratedPlacement {
  skuId: string;
  segments: readonly GeneratedSegment[];
}

export interface GeneratedLayout {
  placements: Readonly<Record<string, GeneratedPlacement>>;
  walkOrder: readonly string[];
  shedFacings?: readonly {
    skuId: string;
    run: number;
    shelf: number;
    requested: number;
    placed: number;
  }[];
  ceilingViolations?: readonly {
    skuId: string;
    run: number;
    shelf: number;
    heightCm: number;
    openingCm: number;
  }[];
}

/** A layout re-expressed on the basis the RENDERER draws. Segment `xCm` is an ANCHOR, not a
 *  position (`layoutToPog` re-derives every final x), so any report of what the plan did must be
 *  measured through a projection. The identity projection means "contract basis". */
export type LayoutProjection = (layout: GeneratedLayout) => GeneratedLayout;

export type FlagKind =
  | "orphan"
  | "split"
  | "ragged"
  | "contiguity"
  | "column"
  | "data"
  | "inversion"
  | "size-order"
  | "he-order"
  | "flavour-order"
  | "ambiguous"
  | "singleton";

export type FlagSeverity = "blocking" | "toCheck" | "toReview";

export const FLAG_SEVERITY: Record<FlagKind, FlagSeverity> = {
  orphan: "blocking",
  split: "blocking",
  ragged: "toCheck",
  contiguity: "blocking",
  // A column break has NO SAFE REPAIR YET, so it is reported for a human: the resolver treats it as
  // an orphan-fragment pin and relocates the whole lower band, splitting more blocks than it fixes.
  column: "toCheck",
  data: "blocking",
  inversion: "toCheck",
  "size-order": "toCheck",
  "he-order": "toCheck",
  "flavour-order": "toReview",
  ambiguous: "toReview",
  singleton: "toReview",
};

export interface FlagEvidence {
  built?: readonly string[];
  want?: readonly string[];
  notes?: readonly string[];
}

export type FixAction =
  | { type: "reorderValues"; attr: string; scopeKey: string; order: readonly string[] }
  | { type: "retagItems"; attr: string; value: string | null; skuIds: readonly string[] };

export interface Flag {
  id: string;
  kind: FlagKind;
  levelLabel?: string;
  severity: FlagSeverity;
  scopePath: readonly string[];
  title: string;
  detail: string;
  skuIds: readonly string[];
  anchorSkuId: string;
  evidence: FlagEvidence;
  fix: FixAction | null;
  fixLabel: string | null;
}

export type RestrictionKind = "overlay" | "breakout" | "split-off" | "shelf-rule";

/** The INTERNAL underscored grammar (`toVariantRestrictionsFileCsv` spells these with spaces on the
 *  way out). The kind cannot imply the condition: one `shelf-rule` is a fence, another reserves. */
export type RestrictionCondition = "CAN_HAVE" | "MUST_HAVE" | "MUST_HAVE_ONLY" | "MUST_NOT_HAVE";

export interface RestrictionCell {
  /** 1-based shelf rank (1 = top, matching the endpoint's "Top"). */
  shelf: number;
  /** 1-based bay numbers (the fixture's `section_num`), ascending. */
  bays: readonly number[];
}

/** PER SHELF: the endpoint takes `bay_no` as a "|"-separated list and `shelf` as ONE term, so a row
 *  is a (shelf, bay-list) pair. Unioning bays across shelves evicts siblings from cells. */
export interface RestrictionPlacement {
  cells: readonly RestrictionCell[];
}

export interface AcceptedRestriction {
  id: string;
  kind: RestrictionKind;
  cdtPath: readonly string[];
  collapsedKey: string | null;
  /** Measured off the SKUs' own placements at finalize time. null = not determinable and NOT
   *  uploaded: a wildcard row is a category-wide outage, a missing row is inert. */
  placement: RestrictionPlacement | null;
  /** Absent keeps the kind-derived mapping (`CAN_HAVE` for split-off and breakout, else `MUST_HAVE`). */
  condition?: RestrictionCondition;
  skuIds: readonly string[];
  source: { origin: "inferred" | "user" | "accepted-violation"; flagId?: string };
  text: string;
}

export interface BrokenBlock {
  node: string;
  foreignFacings: number;
  lanes: readonly string[];
  /** true = introduced by the current draft, false = present in the base plan. */
  introduced: boolean;
}

export interface Provenance {
  broken: readonly BrokenBlock[];
  newBreaks: number;
  preExistingBreaks: number;
  movedSkuIds: readonly string[];
}

export interface CollapsedLevel {
  attr: string;
  label: string;
  sourceAttrs: readonly string[];
  orient: Orientation;
}

export interface CollapsedContract {
  levels: readonly CollapsedLevel[];
  valueOrder: Readonly<Record<string, readonly string[]>>;
  itemValues: Readonly<Record<string, readonly string[]>>;
}

export interface SkuAssignment {
  skuId: string;
  upc: string;
  name: string;
  cdt0: string;
  cdt1: string;
  cdt2: string;
  cdt3: string;
  cdt1Priority: number;
  cdt2Priority: number;
  cdt3Priority: number;
}

export interface LayoutDiff {
  skuId: string;
  before: string;
  after: string;
}

export interface FinalizeResult {
  contract: CollapsedContract;
  verification: {
    identical: boolean;
    orderPreserved: boolean;
    diffs: readonly LayoutDiff[];
  };
  assignments: readonly SkuAssignment[];
  restrictions: readonly AcceptedRestriction[];
  /** Dropped: their CDT0 value is not one this contract ships. Reported, not deleted, so it can be re-made. */
  staleRestrictions: readonly { restriction: AcceptedRestriction; reason: string }[];
}
